import { useHippoText } from '../../../hooks/useHippoText';
import WizardPagetemplate from '../../../templates/WizardPagetemplate';
import CoverableDetailsTemplate from '../../../templates/CoverableDetailsTemplate';
import { SelectField } from '@flos/react-ui';
import { useRecoilState, useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useUpdateCoverableInformation } from '../../../recoil/hooks/useUpdateCoverableInformation';
import { useSaveCoverableInformation } from '../../../hooks/useSaveCoverableInformation';
import { getCoverableIndex } from '../../../utils/coverablesUtil';
import { useState } from 'react';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import type { DrivingExperienceDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/motor';
import _ from 'lodash';
import { carAgeOfPrimaryUserAtom } from '../../../recoil/atoms/carAgeOfPrimaryUserAtom';
import { track } from '../../../utils/trackingUtil';

const minDrivingAge = 18;

const CarSeniorityStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [coverables] = useRecoilState(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const carCoverables = coverables.cars ?? [];
    const coverableIndex = getCoverableIndex(carCoverables, currentProduct.fixedId);
    const currentCarCoverable = carCoverables[coverableIndex];
    const ageOfPrimaryUser = useRecoilValue(carAgeOfPrimaryUserAtom);
    const [selectedDrivingExperience, setSelectedDrivingExperience] = useState<
        DrivingExperienceDTO | undefined
    >(currentCarCoverable.drivingExperience);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const drivingExperienceValid =
        selectedDrivingExperience?.drivingExperienceValue !== undefined &&
        _.isNumber(ageOfPrimaryUser)
            ? ageOfPrimaryUser - minDrivingAge >= selectedDrivingExperience?.drivingExperienceValue
            : undefined;

    const onNext = (): void => {
        setIsLoading(true);
        updateCoverableInformation({
            drivingExperience: selectedDrivingExperience
        });
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            car: {
                publicId: currentCarCoverable.publicId,
                fixedId: currentCarCoverable.fixedId,
                drivingExperienceCode: selectedDrivingExperience?.drivingExperienceTypeKey?.code
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`${currentProduct.flavourName}ContentCoverableInformationDrivingExperience`, [
                {
                    drivingExperienceCode: selectedDrivingExperience?.drivingExperienceTypeKey?.code
                }
            ]);
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onPrevious={goPrevious}
            onNext={onNext}
            disableNext={
                isLoading ||
                selectedDrivingExperience?.drivingExperienceValue === undefined ||
                drivingExperienceValid === false
            }
            disablePrevious={isLoading}
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-SENIORITY-HEADER')}
                subheader={getHippoText('DSS-COVERABLE-INFORMATION-STEP-SENIORITY-TEXT')}
            >
                <SelectField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-SENIORITY-FIELD-TEXT')}
                    onChange={(event) => {
                        const selectedOption = currentCarCoverable.drivingExperienceOptions?.find(
                            (option: DrivingExperienceDTO) => {
                                return (
                                    option?.drivingExperienceTypeKey?.code === event.target.value
                                );
                            }
                        );
                        setSelectedDrivingExperience(selectedOption);
                    }}
                    isValid={drivingExperienceValid}
                    errorText="Antal år i egen bil stemmer ikke overens med brugers alder"
                    value={selectedDrivingExperience?.drivingExperienceTypeKey?.code}
                    options={currentCarCoverable.drivingExperienceOptions?.map(
                        (option: DrivingExperienceDTO) => ({
                            label: option?.drivingExperienceTypeKey?.displayName ?? '',
                            value: option?.drivingExperienceTypeKey?.code ?? ''
                        })
                    )}
                    disabled={isLoading}
                />
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};
export default CarSeniorityStep;
